import React, { useCallback, useEffect, useState } from "react";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Pager from "../../components/Pager";
import SearchSelect from "../../components/SearchSelect";
import { getSites } from "../../services/sites";
import { FilterButton, FilterField, FilterSheet } from "../../components/FilterSheet";
import { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { downloadMigrationBackup, getMigration, getMigrations, rerunMigration, revertMigration, startMigration } from "../../services/migrations";

const STATUS = {
  running: ["Running", ""], done: ["Done", ""], failed: ["Failed", "status-off"], reverting: ["Reverting", "status-off"], reverted: ["Reverted", "status-off"],
};
const MODES = [
  { id: "keep", label: "Keep existing data", hint: "Adds the products and deletes nothing. A product whose name the store already has is added as “Name 1”." },
  { id: "replace", label: "Replace all data", hint: "Takes the store's products and categories out first, then adds the products. Banners and logo stay." },
];
const POLL_MS = 3000;
const when = (value) => new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const host = (url) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");
const NO_FILTERS = { from: "", to: "", source: "", destination: "" };
const BACKUP_DAYS = 2; // the backend keeps a migration's copy of the earlier data this long (services/migration)

// Copy a seller's old website's products into their Ownezzy store. The Super Admin can migrate into any store, a store
// owner only into their own. "Start migration" reads the website and adds its products on the server; this page
// follows its progress. Every migration is kept: it can be run again, and a store's latest one can be reverted.
export default function MigrationTab({ user, notify }) {
  const isSuper = user.role === "superadmin";
  const [form, setForm] = useState({ sourceUrl: "", destinationUrl: "", mode: "keep" });
  const [busy, setBusy] = useState(""); // what is starting or reverting: "new" or a migration id
  const [confirm, setConfirm] = useState(null); // { title, text, action, run }
  const [migrations, setMigrations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(NO_FILTERS); // as applied to the list
  const [filterForm, setFilterForm] = useState(NO_FILTERS); // as typed
  const [filtersOpen, setFiltersOpen] = useState(false); // phones: the filters open in a sheet from the bottom
  const [openId, setOpenId] = useState("");
  const [open, setOpen] = useState(null); // the opened migration, with its problems

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  // Store owners pick one of their own websites (by its address) instead of typing one
  const [destinationSite, setDestinationSite] = useState(null);
  const searchMySites = useCallback(
    (query) => getSites({ search: query, limit: 20, sort: "name" }).then(({ data }) => data.sites.map((site) => ({ value: site.url, label: site.name, hint: host(site.url), site }))),
    []
  );

  const load = useCallback(() => getMigrations(page, filters)
    .then(({ data }) => { setMigrations(data.migrations); setPagination(data.pagination); })
    .catch((err) => notify(errorMessage(err), "error")), [page, filters, notify]);
  const setFilter = (name) => (e) => setFilterForm((f) => ({ ...f, [name]: e.target.value }));
  const applyFilters = (e) => { e.preventDefault(); setPage(1); setFilters(filterForm); };
  const clearFilters = () => { setPage(1); setFilterForm(NO_FILTERS); setFilters(NO_FILTERS); };
  const filtered = Object.values(filters).some(Boolean);
  const filterCount = Object.values(filters).filter(Boolean).length;
  // The sheet's "Show migrations" applies what was typed in it
  const closeSheet = () => { setFiltersOpen(false); setPage(1); setFilters(filterForm); };
  const resetSheet = () => { setFilterForm(NO_FILTERS); };
  const download = async (m) => {
    setBusy(`download-${m._id}`);
    try {
      await downloadMigrationBackup(m);
    } catch (err) {
      notify(err.response && err.response.status === 404 ? `The copy of the earlier data is kept for ${BACKUP_DAYS} days and is no longer available.` : errorMessage(err), "error");
    } finally {
      setBusy("");
    }
  };
  useEffect(() => { load(); }, [load]);

  const show = useCallback((id) => {
    setOpenId(id);
    setOpen(null);
    getMigration(id).then(({ data }) => setOpen(data.migration)).catch((err) => notify(errorMessage(err), "error"));
  }, [notify]);
  const toggle = (id) => (openId === id ? (setOpenId(""), setOpen(null)) : show(id));

  // While one is running, its progress is checked every few seconds
  const running = migrations.some((m) => m.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      load();
      if (openId) getMigration(openId).then(({ data }) => setOpen(data.migration)).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, openId, load]);

  // Runs an action on a migration; `key` names what is busy, so only its button spins
  const act = async (key, request, done) => {
    setConfirm(null);
    setBusy(key);
    try {
      const { data } = await request();
      notify(done(data.migration));
      setPage(1);
      await load();
      show(data.migration._id);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy("");
    }
  };
  const started = (m) => `Migration into ${m.storeName} started`;
  const startNew = () => act("new", () => startMigration({ sourceUrl: form.sourceUrl.trim(), destinationUrl: form.destinationUrl.trim(), mode: form.mode }), started);
  const runAgain = (m) => act(m._id, () => rerunMigration(m._id), started);
  const revert = (m) => act(m._id, () => revertMigration(m._id), (r) => `Migration reverted: ${r.deletedOnRevert} products removed${r.restoredProducts ? `, ${r.restoredProducts} earlier products back` : ""}`);

  const replaceWarning = (store) => ({
    title: "Replace all data?",
    text: `Every product and category of ${store} is taken out before the new products are added. You can bring them back by reverting this migration within ${BACKUP_DAYS} days, or download a copy of them from the migration's details.`,
    action: "Replace and migrate",
  });
  const submit = (e) => {
    e.preventDefault();
    if (!form.destinationUrl.trim()) return notify("Choose the website to copy the products into.", "warn");
    if (form.mode === "replace") setConfirm({ ...replaceWarning(host(form.destinationUrl.trim())), run: startNew });
    else startNew();
  };
  const askRunAgain = (m) => (m.mode === "replace" ? setConfirm({ ...replaceWarning(m.storeName), run: () => runAgain(m) }) : runAgain(m));
  const askRevert = (m) => setConfirm({
    title: "Revert this migration?",
    text: `The ${m.created} products it added to ${m.storeName} are deleted, even if they were edited since${m.mode === "replace" ? ", and the products and categories the store had before it come back" : ""}.`,
    action: "Revert",
    run: () => revert(m),
  });

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">IMPORT</p>
          <h1>Data migration</h1>
          <p className="subheading">{isSuper ? "Copy the products, categories and photos of a seller's old website into their Ownezzy store." : "Copy the products, categories and photos of your old website into your Ownezzy store."}</p>
        </div>
      </div>

      <section className="settings-card migration-card">
        <form onSubmit={submit}>
          <div className="profile-form-grid">
            <label className="admin-field">Source URL (old website)<input required value={form.sourceUrl} onChange={set("sourceUrl")} placeholder="https://example.com" inputMode="url" /></label>
            {isSuper ? (
              <label className="admin-field">Destination URL (Ownezzy store)<input required value={form.destinationUrl} onChange={set("destinationUrl")} placeholder="https://example.com" inputMode="url" /></label>
            ) : (
              <div className="admin-field migration-destination">
                <SearchSelect
                  label="Destination (your website)"
                  placeholder="Search your websites…"
                  emptyText="No website matches"
                  selected={destinationSite}
                  loadOptions={searchMySites}
                  onChange={(option) => { setDestinationSite(option); setForm((f) => ({ ...f, destinationUrl: option.value })); }}
                />
              </div>
            )}
          </div>
          <div className="migration-modes" role="radiogroup" aria-label="The store's current data">
            {MODES.map((m) => (
              <label key={m.id} className={`migration-mode ${form.mode === m.id ? "is-on" : ""}`}>
                <input type="radio" name="mode" value={m.id} checked={form.mode === m.id} onChange={set("mode")} />
                <span><b>{m.label}</b><small>{m.hint}</small></span>
              </label>
            ))}
          </div>
          <p className="field-help">Photos are copied into the store until {isSuper ? "the owner's" : "your"} storage (1 GB) is full; the migration then finishes with the products added so far.</p>
          <button className="primary-btn" disabled={Boolean(busy)}>{busy === "new" ? <><Spinner /> Starting</> : "Start migration"}</button>
        </form>
      </section>

      <div className="migration-warning" role="note">
        <b>Revert is available for only {BACKUP_DAYS} days.</b> Each migration keeps a copy of the store's earlier products and categories for {BACKUP_DAYS} days, then deletes it automatically. Please download the copy of the previous data (Details → Download) to use it later: the website editor's Import can bring it back.
      </div>

      <section className="panel table-panel tab-gap">
        <div className="panel-heading"><div><h2>Migrations</h2><p>Every migration, newest first. A store's latest one can be reverted for {BACKUP_DAYS} days.</p></div></div>
        <form className="table-toolbar migration-filters" onSubmit={applyFilters}>
          <FilterButton count={filterCount} onClick={() => setFiltersOpen(true)} />
          {filtered && <button type="button" className="text-btn migration-clear-phone" onClick={clearFilters}>Clear filters</button>}
          <div className="toolbar-inline-filters">
            <label>From<input type="date" value={filterForm.from} onChange={setFilter("from")} /></label>
            <label>To<input type="date" value={filterForm.to} onChange={setFilter("to")} /></label>
            <input placeholder="Source URL" value={filterForm.source} onChange={setFilter("source")} aria-label="Filter by source URL" />
            <input placeholder="Destination URL or store" value={filterForm.destination} onChange={setFilter("destination")} aria-label="Filter by destination URL" />
            <button className="outline-btn">Filter</button>
            {filtered && <button type="button" className="text-btn" onClick={clearFilters}>Clear</button>}
          </div>
        </form>
        <DataTable>
          <thead><tr><th>STARTED</th><th>SOURCE</th><th>STORE</th><th>DATA</th><th>STATUS</th><th>ADDED</th><th></th></tr></thead>
          <tbody>
            {migrations.map((m) => (
              <tr key={m._id}>
                <td>{when(m.createdAt)}</td>
                <td>{host(m.sourceUrl)}</td>
                <td><b>{m.storeName}</b><small className="cell-sub">{m.storeSlug}</small></td>
                <td>{m.mode === "replace" ? "Replaced" : "Kept"}</td>
                <td><span className={`status-pill ${STATUS[m.status][1]}`}><i />{STATUS[m.status][0]}</span><small className="cell-sub">{m.status === "failed" ? m.message : m.stage}</small></td>
                <td>{m.created} of {m.found}</td>
                <td>
                  <div className="row-actions">
                    <button className="edit-action" onClick={() => toggle(m._id)}>{openId === m._id ? "Hide" : "Details"}</button>
                    {!["running", "reverting"].includes(m.status) && <button className="toggle-action" disabled={Boolean(busy)} onClick={() => askRunAgain(m)}>{busy === m._id ? <Spinner /> : "Re-run"}</button>}
                    {m.canRevert && <button className="toggle-action migration-revert" disabled={Boolean(busy)} onClick={() => askRevert(m)}>Revert</button>}
                    {m.backupAvailable && <button className="toggle-action" disabled={Boolean(busy)} onClick={() => download(m)} title="The store's data from before this migration, as Excel">{busy === `download-${m._id}` ? <Spinner /> : "⇩ Download"}</button>}
                  </div>
                </td>
              </tr>
            ))}
            {!migrations.length && <tr><td colSpan="7" className="empty-row">{filtered ? "No migrations match these filters." : "No migrations yet."}</td></tr>}
          </tbody>
        </DataTable>
        {pagination && <Pager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} noun="migrations" />}
      </section>

      {openId && (
        <section className="panel info-panel tab-gap">
          {!open ? <Spinner /> : (
            <>
              <h2>{open.storeName}: from {host(open.sourceUrl)}</h2>
              {open.status === "running" && <p>{open.stage}…</p>}
              {open.status === "failed" && <p>{open.message}</p>}
              {["done", "reverted"].includes(open.status) && (
                <p>
                  {open.mode === "replace" && `Took out ${open.removedProducts} products and ${open.removedCategories} categories first. `}
                  Added {open.created} of the {open.found} products found and {open.categoriesCreated} categories, with {open.imagesCopied} photos{open.withoutImage ? ` (${open.withoutImage} without a photo)` : ""}.
                  {open.renamed > 0 && ` ${open.renamed} got a number after their name because the store already had it.`}
                  {open.stoppedAtStorageLimit && <b> The storage is full, so the migration stopped there. Free up space and run it again to add the rest.</b>}
                </p>
              )}
              {open.backupAvailable && (
                <div className="migration-backup">
                  <span>{open.canRevert ? <>You can revert this migration until <b>{when(open.revertUntil)}</b>. </> : null}A copy of the store's data from before it is kept until <b>{when(open.revertUntil)}</b>, then deleted. Download it to keep it.</span>
                  <button className="outline-btn" disabled={Boolean(busy)} onClick={() => download(open)}>{busy === `download-${open._id}` ? <Spinner /> : "⇩"} Download previous data (Excel)</button>
                </div>
              )}
              {!open.backupAvailable && open.revertUntil && open.status !== "running" && <p className="field-help">The copy of the earlier data was kept for {BACKUP_DAYS} days and has been deleted.</p>}
              {open.status === "reverted" && <p><b>Reverted {when(open.revertedAt)}:</b> {open.deletedOnRevert} products removed{open.restoredProducts ? `, ${open.restoredProducts} earlier products and ${open.restoredCategories} categories back` : ""}.</p>}
              {open.problemCount > 0 && (
                <>
                  <p><b>{open.problemCount} {open.problemCount === 1 ? "page or photo" : "pages or photos"} could not be used</b>{open.problemCount > open.problems.length ? ` (first ${open.problems.length} shown)` : ""}:</p>
                  <ul className="migration-problems">
                    {open.problems.map((p, i) => <li key={`${p.url}-${i}`}><a href={p.url} target="_blank" rel="noopener noreferrer">{p.url}</a> {p.message}</li>)}
                  </ul>
                </>
              )}
            </>
          )}
        </section>
      )}

      {filtersOpen && (
        <FilterSheet title="Filter migrations" resultLabel="Show migrations" onClose={closeSheet} onReset={resetSheet}>
          <div className="filter-dates">
            <FilterField label="From"><input type="date" value={filterForm.from} onChange={setFilter("from")} /></FilterField>
            <FilterField label="To"><input type="date" value={filterForm.to} onChange={setFilter("to")} /></FilterField>
          </div>
          <FilterField label="Source URL"><input value={filterForm.source} onChange={setFilter("source")} placeholder="https://example.com" /></FilterField>
          <FilterField label="Destination URL or store"><input value={filterForm.destination} onChange={setFilter("destination")} placeholder="https://example.com" /></FilterField>
        </FilterSheet>
      )}

      {confirm && (
        <Modal eyebrow="DATA MIGRATION" title={confirm.title} description={confirm.text} onClose={() => setConfirm(null)}>
          <div className="product-modal-actions">
            <button type="button" className="outline-btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className="danger-btn" onClick={confirm.run}>{confirm.action}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
